import { useState, useEffect, useRef, useCallback } from "react";
import { TrendingUp, TrendingDown, Wifi, WifiOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Realistic simulation for stock indices ── */
const BASE_PRICES = {
  "S&P 500": { price: 6856, vol: 0.008 },
  "NASDAQ": { price: 23094, vol: 0.012 },
  "DOW JONES": { price: 48020, vol: 0.006 },
};

const simulateIndex = (name, prevPrice) => {
  const base = BASE_PRICES[name];
  const drift = (Math.random() - 0.48) * base.price * base.vol;
  const price = (prevPrice || base.price) + drift;
  return Math.round(price * 100) / 100;
};

/* ── Direct CoinGecko call (CORS enabled) ── */
const fetchBTC = async () => {
  const resp = await fetch(
    "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd&include_24hr_change=true"
  );
  const data = await resp.json();
  const btc = data.bitcoin || {};
  return {
    symbol: "BTC/USD",
    price: btc.usd || 0,
    change_pct: btc.usd_24h_change || 0,
    history: [],
    source: "live",
  };
};

/* ── Try backend API (works on Emergent, fails on static deploy) ── */
const fetchBackendStocks = async () => {
  const resp = await fetch(`${BACKEND_URL}/api/stocks`);
  const data = await resp.json();
  return data.data || [];
};

/* ── Sparkline ── */
const MiniSparkline = ({ data, up }) => {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const h = 20, w = 60;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="shrink-0">
      <polyline points={pts} fill="none" stroke={up ? "rgba(52,211,153,0.7)" : "rgba(239,68,68,0.7)"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

/* ── Component ── */
const StockTicker = () => {
  const [stocks, setStocks] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const historyRef = useRef({});
  const scrollRef = useRef(null);

  const fetchAll = useCallback(async () => {
    // Strategy 1: Try backend (has Yahoo Finance proxy)
    try {
      const backendData = await fetchBackendStocks();
      if (backendData.length >= 3) {
        // Store history for sparklines
        backendData.forEach((s) => {
          if (!historyRef.current[s.symbol]) historyRef.current[s.symbol] = [];
          historyRef.current[s.symbol].push(s.price);
          if (historyRef.current[s.symbol].length > 30) historyRef.current[s.symbol].shift();
        });
        setStocks(
          backendData.map((s) => ({
            ...s,
            history: s.history?.length > 1 ? s.history : historyRef.current[s.symbol] || [],
          }))
        );
        setIsLive(true);
        return;
      }
    } catch {
      /* backend not available — fallback below */
    }

    // Strategy 2: CoinGecko for BTC + simulated indices
    try {
      const btc = await fetchBTC();
      const indices = Object.entries(BASE_PRICES).map(([name]) => {
        const prev = historyRef.current[name]?.slice(-1)[0];
        const price = simulateIndex(name, prev);
        if (!historyRef.current[name]) historyRef.current[name] = [];
        historyRef.current[name].push(price);
        if (historyRef.current[name].length > 30) historyRef.current[name].shift();
        const hist = historyRef.current[name];
        const first = hist[0] || price;
        return {
          symbol: name,
          price,
          change_pct: Math.round(((price - first) / first) * 10000) / 100,
          history: hist,
          source: "simulated",
        };
      });

      if (!historyRef.current["BTC/USD"]) historyRef.current["BTC/USD"] = [];
      historyRef.current["BTC/USD"].push(btc.price);
      if (historyRef.current["BTC/USD"].length > 30) historyRef.current["BTC/USD"].shift();
      btc.history = historyRef.current["BTC/USD"];

      setStocks([...indices, btc]);
      setIsLive(true); // BTC is live
    } catch {
      setIsLive(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 60000); // 60s
    return () => clearInterval(iv);
  }, [fetchAll]);

  if (stocks.length === 0) return null;

  const allItems = [...stocks, ...stocks];

  return (
    <div data-testid="stock-ticker" className="w-full z-20 relative overflow-hidden">
      <div
        ref={scrollRef}
        className="flex items-center gap-6 whitespace-nowrap py-1.5 px-4"
        style={{ animation: "marquee 30s linear infinite" }}
      >
        {allItems.map((s, idx) => {
          const up = (s.change_pct || 0) >= 0;
          const isCrypto = s.symbol === "BTC/USD";
          return (
            <div
              key={`${s.symbol}-${idx}`}
              data-testid={`ticker-${s.symbol.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
              className="flex items-center gap-3 shrink-0"
            >
              <span className="font-heading text-[9px] tracking-[0.15em] text-cyan-400 uppercase">
                {s.symbol}
              </span>
              <span className="font-mono text-[10px] text-cyan-200 tracking-wider number-tick">
                {isCrypto
                  ? `$${s.price.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                  : s.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span
                className={`flex items-center gap-0.5 font-mono text-[9px] tracking-wider ${up ? "text-emerald-400" : "text-red-400"}`}
              >
                {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {up ? "+" : ""}{(s.change_pct || 0).toFixed(2)}%
              </span>
              {s.history?.length > 1 && <MiniSparkline data={s.history} up={up} />}
              <div className="w-[1px] h-3 bg-cyan-500/20" />
            </div>
          );
        })}
        <div className="flex items-center gap-1 shrink-0">
          {isLive ? <Wifi className="w-2.5 h-2.5 text-emerald-400" /> : <WifiOff className="w-2.5 h-2.5 text-cyan-600" />}
          <span className={`font-mono text-[7px] tracking-wider ${isLive ? "text-emerald-400" : "text-cyan-600"}`}>
            {isLive ? "LIVE" : "OFFLINE"}
          </span>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
    </div>
  );
};

export default StockTicker;
